import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { File, FileSpreadsheet, FileText, Image } from 'lucide-react';

type FileIconProps = {
  mimetype?: string | null;
  filename?: string;
  className?: string;
  size?: number | string | 'small' | 'medium' | 'large' | 'inherit';
};

const resolveSize = (size: FileIconProps['size']): number | string => {
  if (typeof size === 'number' || typeof size === 'string') {
    switch (size) {
      case 'small':
        return 14;
      case 'medium':
        return 16;
      case 'large':
        return 18;
      case 'inherit':
        return '1em';
      default:
        return size;
    }
  }

  return 16;
};

const iconConfig = (
  mimetype: string | null | undefined,
  filename: string | undefined,
): { icon: typeof File; color: string } => {
  const lowerFilename = filename?.toLowerCase() ?? '';

  if (mimetype?.includes('pdf')) {
    return { icon: FileText, color: '#EF4444' };
  }

  if (mimetype?.startsWith('image/')) {
    return { icon: Image, color: '#3B82F6' };
  }

  if (
    mimetype?.includes('spreadsheet') ||
    mimetype?.includes('ms-excel') ||
    lowerFilename.match(/\.xlsx?$|\.ods$/)
  ) {
    return { icon: FileSpreadsheet, color: '#22C55E' };
  }

  if (
    mimetype?.includes('word') ||
    mimetype?.includes('msword') ||
    lowerFilename.match(/\.docx?$|\.odt$/)
  ) {
    return { icon: FileText, color: '#3B82F6' };
  }

  return { icon: File, color: '#6B7280' };
};

export const FileIcon = ({
  mimetype,
  filename,
  className,
  size = 16,
}: FileIconProps): JSX.Element => {
  const { icon: Icon, color } = iconConfig(mimetype, filename);
  const iconSize = resolveSize(size);

  if (filename == null) {
    return <Icon className={className} size={iconSize} color={color} />;
  }

  return (
    <Box
      className={className}
      sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        bgcolor: alpha(color, 0.1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={iconSize} color={color} />
    </Box>
  );
};
